/**
 * Pause menu — a left-edge overlay drawer, opened by ESC (or any pointer-lock exit while flying).
 *
 * Layout: a fixed-width column (1/3 of the viewport) that slides + fades in from the left. Options
 * are plain text rows (no button chrome). It holds two stacked VIEWS that cross-fade in place —
 * the main list and an "Audio & Video" settings pane — and pins LOG OUT to the bottom.
 */
import { audio } from "../audio";

export class PauseMenu {
  private el: HTMLElement;

  constructor(handlers: {
    onResume: () => void;
    onLogout: () => void;
    /** Open the Traveler's Log (charted systems). */
    onLog: () => void;
    /** Open the controls reference. */
    onControls: () => void;
    /** Open the god-mode panel (row hidden until setGodMode(true)). */
    onGod: () => void;
    /** Terrain draw-distance quality (0.6..2.5). */
    quality: number;
    /** Drawing-buffer scale, 0.5..1 of native. The frame is fill-bound, so this
     *  moves performance further than anything else in this pane. */
    resScale: number;
    onResScale: (s: number) => void;
    invertY: boolean;
    onInvertY: (on: boolean) => void;
    /** Live network line, polled when the menu opens. */
    netStats: () => string;
    onQuality: (q: number) => void;
  }) {
    this.el = document.createElement("div");
    this.el.id = "pause-menu";
    this.el.dataset.view = "main";
    this.el.innerHTML = `
      <aside class="pm-panel" role="dialog" aria-label="Game menu">
        <div class="pm-brand"><span class="pm-eyebrow">PAUSED</span></div>

        <div class="pm-body">
          <nav class="pm-view pm-main">
            <button class="pm-item" data-action="resume">Resume</button>
            <button class="pm-item" data-action="log">Traveler's Log</button>
            <button class="pm-item" data-action="controls">Controls</button>
            <button class="pm-item" data-action="av">Audio &amp; Video</button>
            <button class="pm-item" id="pm-fullscreen" data-action="fullscreen">Fullscreen<span class="pm-item-state" id="pm-fullscreen-state"></span></button>
            <button class="pm-item pm-item-god" id="pm-god" data-action="god" style="display:none">God Mode</button>
            <div class="pm-hint">Flight continues for other pilots while you're here. The shipyard lives in the C menu.</div>
          </nav>

          <section class="pm-view pm-av" aria-label="Audio and video settings">
            <button class="pm-back" data-action="back"><span aria-hidden="true">‹</span> Audio &amp; Video</button>

            <div class="pm-group">
              <div class="pm-group-title">Video</div>
              <label class="pm-slider">
                <span class="pm-slider-head">Resolution <em id="pm-res-val"></em></span>
                <input id="pm-res" type="range" min="0.5" max="1" step="0.05" />
              </label>
              <label class="pm-slider">
                <span class="pm-slider-head">Detail distance <em id="pm-q-val"></em></span>
                <input id="pm-quality" type="range" min="0.6" max="2.5" step="0.1" />
              </label>
              <button class="pm-toggle" id="pm-inverty" data-action="inverty">
                <span>Invert Y axis</span><span class="pm-toggle-state" id="pm-inverty-state"></span>
              </button>
            </div>

            <div class="pm-group">
              <div class="pm-group-title">Audio</div>
              <label class="pm-slider">
                <span class="pm-slider-head">Master <em id="pm-vol-master"></em></span>
                <input id="pm-vol-master-r" type="range" min="0" max="1" step="0.05" />
              </label>
              <label class="pm-slider">
                <span class="pm-slider-head">Music <em id="pm-vol-music"></em></span>
                <input id="pm-vol-music-r" type="range" min="0" max="1" step="0.05" />
              </label>
              <label class="pm-slider">
                <span class="pm-slider-head">Effects <em id="pm-vol-fx"></em></span>
                <input id="pm-vol-fx-r" type="range" min="0" max="1" step="0.05" />
              </label>
              <button class="pm-toggle" id="pm-mute" data-action="mute">
                <span>Sound</span><span class="pm-toggle-state" id="pm-mute-state"></span>
              </button>
              <a href="/audio/place/credits.html" target="_blank" rel="noopener" class="pm-item">Music instrument credits ↗</a>
            </div>
          </section>
        </div>

        <footer class="pm-foot">
          <div class="pm-net" id="pm-net"></div>
          <button class="pm-item pm-logout" data-action="logout">Log out</button>
        </footer>
      </aside>
    `;
    document.body.appendChild(this.el);

    const on = (action: string, fn: () => void): void =>
      this.el.querySelector(`[data-action="${action}"]`)!.addEventListener("click", fn);
    on("resume", handlers.onResume);
    on("logout", handlers.onLogout);
    on("log", handlers.onLog);
    on("controls", handlers.onControls);
    on("av", () => this.setView("av"));
    on("back", () => this.setView("main"));
    this.godBtn = this.el.querySelector<HTMLElement>("#pm-god")!;
    this.godBtn.addEventListener("click", handlers.onGod);

    // Fullscreen is pure browser state — no game plumbing. requestFullscreen needs a
    // user gesture, which the click is. The label tracks `fullscreenchange` so F11
    // and the browser's own Esc-out stay honest, AND re-reads once the request
    // settles: some engines flip document.fullscreenElement without firing the
    // event, which left the row reading OFF in fullscreen.
    const fsState = this.el.querySelector<HTMLElement>("#pm-fullscreen-state")!;
    this.showFs = (): void => {
      const on = document.fullscreenElement !== null;
      fsState.textContent = on ? "ON" : "OFF";
      fsState.classList.toggle("is-off", !on);
    };
    this.showFs();
    document.addEventListener("fullscreenchange", this.showFs);
    on("fullscreen", () => {
      const settled = document.fullscreenElement
        ? document.exitFullscreen()
        : document.documentElement.requestFullscreen();
      void Promise.resolve(settled).catch(() => {}).then(() => this.showFs());
    });

    // Resolution first: it is listed above detail distance because it is the
    // larger lever by a wide margin (the frame is GPU-fill bound). The readout
    // shows the resulting drawing buffer, not just a percentage — "83%" means
    // nothing on its own, "2656x1660" is the number that costs the milliseconds.
    const resSlider = this.el.querySelector<HTMLInputElement>("#pm-res")!;
    const resLabel = this.el.querySelector<HTMLElement>("#pm-res-val")!;
    const showRes = (s: number): void => {
      // Mirrors main.ts applyRenderScale exactly, including the devicePixelRatio
      // cap of 2 — otherwise the readout would promise a buffer we never make.
      const r = Math.min(window.devicePixelRatio, 2) * s;
      const w = Math.round(window.innerWidth * r);
      const h = Math.round(window.innerHeight * r);
      resLabel.textContent = `${Math.round(s * 100)}% \u00b7 ${w}\u00d7${h}`;
    };
    resSlider.value = String(handlers.resScale);
    showRes(handlers.resScale);
    resSlider.addEventListener("input", () => {
      const s = parseFloat(resSlider.value);
      showRes(s);
      handlers.onResScale(s);
    });
    // A window resize changes what any given percentage buys.
    window.addEventListener("resize", () => showRes(parseFloat(resSlider.value)));

    const slider = this.el.querySelector<HTMLInputElement>("#pm-quality")!;
    const qLabel = this.el.querySelector<HTMLElement>("#pm-q-val")!;
    const showQ = (q: number): void => {
      qLabel.textContent = `${Math.round(q * 100)}%`;
    };
    slider.value = String(handlers.quality);
    showQ(handlers.quality);
    slider.addEventListener("input", () => {
      const q = parseFloat(slider.value);
      showQ(q);
      handlers.onQuality(q);
    });

    let inv = handlers.invertY;
    const invState = this.el.querySelector<HTMLElement>("#pm-inverty-state")!;
    const showInv = (): void => {
      invState.textContent = inv ? "On" : "Off";
      invState.classList.toggle("is-off", !inv);
    };
    showInv();
    this.el.querySelector("#pm-inverty")!.addEventListener("click", () => {
      inv = !inv;
      showInv();
      handlers.onInvertY(inv);
    });

    // Audio mix — reads/writes the audio facade directly (it owns persistence).
    const prefs = audio.prefs();
    const bindVol = (id: string, init: number, apply: (v: number) => void): void => {
      const r = this.el.querySelector<HTMLInputElement>(`#pm-vol-${id}-r`)!;
      const lab = this.el.querySelector<HTMLElement>(`#pm-vol-${id}`)!;
      const show = (v: number): void => {
        lab.textContent = `${Math.round(v * 100)}%`;
      };
      r.value = String(init);
      show(init);
      r.addEventListener("input", () => {
        const v = parseFloat(r.value);
        show(v);
        apply(v);
      });
    };
    bindVol("master", prefs.master, (v) => audio.setMasterVolume(v));
    bindVol("music", prefs.music, (v) => audio.setMusicVolume(v));
    bindVol("fx", prefs.fx, (v) => audio.setFxVolume(v));

    let muted = prefs.muted;
    const muteState = this.el.querySelector<HTMLElement>("#pm-mute-state")!;
    const showMute = (): void => {
      muteState.textContent = muted ? "Muted" : "On";
      muteState.classList.toggle("is-off", muted);
    };
    showMute();
    this.el.querySelector("#pm-mute")!.addEventListener("click", () => {
      muted = !muted;
      showMute();
      audio.setMuted(muted);
    });

    this.netStats = handlers.netStats;

    // Click-outside-to-retract: the scrim is pointer-events:none (only .pm-panel is
    // interactive), so a click anywhere but the drawer lands here — treat it exactly like
    // Resume. Capture phase + swallow the press so it retracts ONLY: without this the same
    // mousedown reaches the canvas's weapon-button listener (input.ts) and would fire on
    // dismiss, since onResume() flips gameplay mode back synchronously. Opening is via ESC /
    // pointer-lock exit (never a mousedown), so this can't self-close.
    const panel = this.el.querySelector<HTMLElement>(".pm-panel")!;
    document.addEventListener(
      "mousedown",
      (e) => {
        if (!this.visible || panel.contains(e.target as Node)) return;
        e.preventDefault();
        e.stopPropagation();
        handlers.onResume();
      },
      true,
    );
  }

  private netStats: () => string = () => "";
  private godBtn!: HTMLElement;
  private showFs: () => void = () => {};

  private setView(view: "main" | "av"): void {
    this.el.dataset.view = view;
  }

  /** Reveal/hide the GOD MODE row (server-decided; default hidden). */
  setGodMode(on: boolean): void {
    this.godBtn.style.display = on ? "" : "none";
  }

  get visible(): boolean {
    return this.el.classList.contains("open");
  }

  /**
   * ESC steps back one level: from the Audio/Video pane to the main list. Returns true if it
   * consumed the press (so the caller does NOT also close the whole menu).
   */
  backOut(): boolean {
    if (this.el.dataset.view === "av") {
      this.setView("main");
      return true;
    }
    return false;
  }

  show(): void {
    this.setView("main"); // always open on the main list
    this.showFs(); // fullscreen may have been toggled by F11 while the menu was closed
    const net = this.el.querySelector<HTMLElement>("#pm-net");
    if (net) net.textContent = this.netStats();
    this.el.classList.add("open");
    // Focus the first option so the menu is keyboard-navigable from the moment it opens.
    this.el.querySelector<HTMLElement>(".pm-main .pm-item")?.focus();
  }

  hide(): void {
    this.el.classList.remove("open");
  }
}
